import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent
} from '@mui/material';
import MainLayout from '../../layout/MainLayout';
import ChatInterface from '../../components/omop/ChatInterface';
import ResultsPanel from '../../components/omop/ResultsPanel';

const OMOPChatPage = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'system',
      content: 'Willkommen beim OMOP Data Explorer! Stellen Sie Fragen zu unseren Patientendaten.',
      timestamp: new Date()
    }
  ]);
  const [currentResult, setCurrentResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSendMessage = async (message) => {
    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setLoading(true);

    // Simulate API delay
    setTimeout(() => {
      const mockResponse = generateMockResponse(message);
      const systemMessage = {
        id: messages.length + 2,
        type: 'system',
        content: mockResponse.response_text,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, systemMessage]);
      setCurrentResult(mockResponse);
      setLoading(false);
    }, 1500);
  };

  const generateMockResponse = (message) => {
    const lowerMessage = message.toLowerCase();

    if (lowerMessage.includes('brustkrebs') || lowerMessage.includes('breast cancer')) {
      return {
        response_text: 'Gefunden: 7 Brustkrebspatienten (Alter 45-68 Jahre). Die Daten zeigen verschiedene Behandlungsverläufe mit Tamoxifen, Paclitaxel und anderen Therapien.',
        result_type: 'table',
        data: {
          headers: ['Patient ID', 'Alter', 'Geschlecht', 'Diagnose', 'Behandlung'],
          rows: [
            ['P001', '52', 'Weiblich', 'Invasives Mammakarzinom', 'Tamoxifen'],
            ['P002', '68', 'Weiblich', 'Duktales Karzinom', 'Paclitaxel + Cisplatin'],
            ['P003', '45', 'Weiblich', 'HER2+ Brustkrebs', 'Trastuzumab'],
            ['P004', '59', 'Weiblich', 'Invasives Mammakarzinom', 'Tamoxifen'],
            ['P005', '61', 'Weiblich', 'Duktales Karzinom', 'Chemotherapie'],
            ['P006', '55', 'Weiblich', 'Invasives Mammakarzinom', 'Hormontherapie'],
            ['P007', '63', 'Weiblich', 'HER2+ Brustkrebs', 'Trastuzumab + Chemotherapie']
          ]
        },
        suggestions: ['Zeige Behandlungsverläufe', 'Filtere nach Alter über 60', 'Zeige Überlebensraten']
      };
    } else if (lowerMessage.includes('lungenkrebs') || lowerMessage.includes('lung cancer')) {
      return {
        response_text: 'Gefunden: 6 Lungenkrebspatienten (Alter 52-74 Jahre). Verschiedene Stadien und Behandlungsansätze dokumentiert.',
        result_type: 'chart',
        data: {
          type: 'bar',
          title: 'Lungenkrebspatienten nach Alter',
          data: [
            { name: '50-60', value: 2 },
            { name: '60-70', value: 3 },
            { name: '70-80', value: 1 }
          ]
        },
        suggestions: ['Zeige Behandlungen', 'Stadium-Verteilung', 'Raucherstatus']
      };
    } else if (lowerMessage.includes('statistik') || lowerMessage.includes('übersicht')) {
      return {
        response_text: 'Patientenübersicht: 524 Patienten insgesamt, davon 14 Krebspatienten (2.7%). Die häufigsten Krebsarten sind Brustkrebs (7), Lungenkrebs (6) und Kolorektalkrebs (5).',
        result_type: 'summary',
        data: {
          stats: [
            { label: 'Gesamtpatienten', value: 524 },
            { label: 'Krebspatienten', value: 14 },
            { label: 'Brustkrebspatienten', value: 7 },
            { label: 'Lungenkrebspatienten', value: 6 },
            { label: 'Kolorektalkrebspatienten', value: 5 }
          ]
        },
        suggestions: ['Zeige Brustkrebspatienten', 'Zeige Lungenkrebspatienten', 'Altersverteilung']
      };
    } else {
      return {
        response_text: 'Ich verstehe Ihre Anfrage. Hier sind einige Beispiele für mögliche Abfragen mit unseren OMOP-Daten.',
        result_type: 'summary',
        data: {
          examples: [
            'Zeige alle Brustkrebspatienten',
            'Lungenkrebspatienten nach Alter',
            'Statistik über alle Krebspatienten',
            'Behandlungsverläufe für HER2+ Patienten',
            'Medikamente bei Chemotherapie'
          ]
        },
        suggestions: ['Zeige Brustkrebspatienten', 'Patientenstatistik', 'Alle Krebsarten']
      };
    }
  };

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1, p: 3, height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h4" gutterBottom component="div" sx={{ fontWeight: 'bold', mb: 3 }}>
          OMOP Data Explorer
        </Typography>

        <Grid container spacing={3} sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
                <ChatInterface
                  messages={messages}
                  onSendMessage={handleSendMessage}
                  loading={loading}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <ResultsPanel result={currentResult} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </MainLayout>
  );
};

export default OMOPChatPage;